import React, {useState} from "react"
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Textarea,
  Input,
  Spinner,
} from "@heroui/react"
import {IconUser, IconMessageCircle} from "@tabler/icons-react"

interface AlertActionModalProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  title: string
  action: "comment" | "assign"
  loading?: boolean
  onSubmit: (data: {comment?: string; assignedTo?: string}) => Promise<boolean>
}

export const AlertActionModal: React.FC<AlertActionModalProps> = ({
  isOpen,
  onOpenChange,
  title,
  action,
  loading = false,
  onSubmit,
}) => {
  const [comment, setComment] = useState("")
  const [assignedTo, setAssignedTo] = useState("")

  const handleSubmit = async () => {
    const data: {comment?: string; assignedTo?: string} = {}

    if (action === "comment" || comment.trim()) {
      data.comment = comment.trim()
    }

    if (action === "assign") {
      data.assignedTo = assignedTo.trim()
    }

    const success = await onSubmit(data)

    if (success) {
      // Reset form and close modal
      setComment("")
      setAssignedTo("")
      onOpenChange(false)
    }
  }

  const isValid =
    action === "comment"
      ? comment.trim().length > 0
      : action === "assign"
        ? assignedTo.trim().length > 0
        : true

  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange} placement="top-center">
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                {action === "comment" ? (
                  <IconMessageCircle className="h-5 w-5 text-blue-600" />
                ) : (
                  <IconUser className="h-5 w-5 text-purple-600" />
                )}
                {title}
              </div>
            </ModalHeader>
            <ModalBody>
              {action === "assign" && (
                <Input
                  label="Assign to"
                  placeholder="Enter email or username"
                  value={assignedTo}
                  onValueChange={setAssignedTo}
                  startContent={<IconUser className="h-4 w-4 text-gray-400" />}
                  disabled={loading}
                />
              )}

              <Textarea
                label={
                  action === "comment"
                    ? "Comment"
                    : "Additional comment (optional)"
                }
                placeholder={
                  action === "comment"
                    ? "Add your comment about this alert..."
                    : "Add any additional notes..."
                }
                value={comment}
                onValueChange={setComment}
                minRows={3}
                disabled={loading}
                isRequired={action === "comment"}
              />
            </ModalBody>
            <ModalFooter>
              <Button
                color="danger"
                variant="light"
                onPress={onClose}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                color="primary"
                onPress={handleSubmit}
                disabled={!isValid || loading}
                startContent={loading ? <Spinner size="sm" /> : undefined}
              >
                {loading
                  ? "Processing..."
                  : action === "comment"
                    ? "Add Comment"
                    : "Assign Alert"}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  )
}
